/* `/knowledge` — bases, their tree, and which agents may read them.
   CONTRACT §5 for the wire, §11 for the rules this file follows.

   It lives in `shared/api/` rather than beside a feature because three features
   read it: the Knowledge page, the agent Configure panel and the chat header's
   document count (rule book §1 rule 5 — a feature never imports a feature).
   `shared/api/settings.ts` is the same case and the same shape.

   Component → hook → api function → client, one direction. Paths are bare;
   `client.baseURL` already carries `/api/v1`. Every response goes through
   `unwrap` except the raw file stream, which has no envelope to read. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";
import { threadKeys } from "./threads";

import type {
  KbContent,
  KbCreate,
  KbFileUrl,
  KbFolderCreate,
  KbLink,
  KbLinkMode,
  KbNode,
  KbNodePatch,
  KbNoteCreate,
  KbPatch,
  KbRemoved,
  KbSearchHit,
  KbUnlinked,
  KbUpload,
  KnowledgeBase,
} from "@/shared/types";

export const knowledgeKeys = {
  all: ["knowledge"] as const,
  list: (agentId?: string) => [...knowledgeKeys.all, "list", agentId ?? null] as const,
  detail: (kbId: string) => [...knowledgeKeys.all, "detail", kbId] as const,
  nodes: (kbId: string) => [...knowledgeKeys.all, "nodes", kbId] as const,
  search: (kbId: string, q: string) => [...knowledgeKeys.all, "search", kbId, q] as const,
  agents: (kbId: string) => [...knowledgeKeys.all, "agents", kbId] as const,
  content: (kbId: string, nodeId: string) => [...knowledgeKeys.all, "content", kbId, nodeId] as const,
};

/* ------------------------------- bases ----------------------------------- */

/** With `agentId`, only the bases linked to that agent; without, the org's. */
export async function listKnowledgeBases(agentId?: string): Promise<KnowledgeBase[]> {
  return unwrap(await client.get<Envelope<KnowledgeBase[]>>("/knowledge", { params: { agent_id: agentId } }));
}

export async function createKnowledgeBase(body: KbCreate): Promise<KnowledgeBase> {
  return unwrap(await client.post<Envelope<KnowledgeBase>>("/knowledge", body));
}

export async function getKnowledgeBase(kbId: string): Promise<KnowledgeBase> {
  return unwrap(await client.get<Envelope<KnowledgeBase>>(`/knowledge/${kbId}`));
}

export async function patchKnowledgeBase(kbId: string, body: KbPatch): Promise<KnowledgeBase> {
  return unwrap(await client.patch<Envelope<KnowledgeBase>>(`/knowledge/${kbId}`, body));
}

export async function deleteKnowledgeBase(kbId: string): Promise<KbRemoved> {
  return unwrap(await client.delete<Envelope<KbRemoved>>(`/knowledge/${kbId}`));
}

/* ------------------------------- nodes ----------------------------------- */

/** The whole tree, flat — the client builds the nesting from `parent_id`. */
export async function listKbNodes(kbId: string): Promise<KbNode[]> {
  return unwrap(await client.get<Envelope<KbNode[]>>(`/knowledge/${kbId}/nodes`));
}

export async function createKbFolder(kbId: string, body: KbFolderCreate): Promise<KbNode> {
  return unwrap(await client.post<Envelope<KbNode>>(`/knowledge/${kbId}/folders`, body));
}

/**
 * Upload a batch. `files` and `paths` are repeated in step, so `paths[i]` names
 * `files[i]`; a path may nest ("specs/x.json") and the missing folders are
 * created server-side. One transaction per batch — 200 files, 10 MB each.
 */
export async function uploadKbFiles(kbId: string, uploads: KbUpload[], parentId?: string): Promise<KbNode[]> {
  const form = new FormData();
  for (const { file, path } of uploads) {
    form.append("files", file);
    form.append("paths", path ?? file.name);
  }
  if (parentId) form.append("parent_id", parentId);
  return unwrap(await client.post<Envelope<KbNode[]>>(`/knowledge/${kbId}/files`, form));
}

export async function createKbNote(kbId: string, body: KbNoteCreate): Promise<KbNode> {
  return unwrap(await client.post<Envelope<KbNode>>(`/knowledge/${kbId}/notes`, body));
}

export async function searchKb(kbId: string, q: string, limit = 20): Promise<KbSearchHit[]> {
  return unwrap(await client.get<Envelope<KbSearchHit[]>>(`/knowledge/${kbId}/search`, { params: { q, limit } }));
}

export async function patchKbNode(kbId: string, nodeId: string, body: KbNodePatch): Promise<KbNode> {
  return unwrap(await client.patch<Envelope<KbNode>>(`/knowledge/${kbId}/nodes/${nodeId}`, body));
}

/** Deletes the node and everything under it. */
export async function deleteKbNode(kbId: string, nodeId: string): Promise<KbRemoved> {
  return unwrap(await client.delete<Envelope<KbRemoved>>(`/knowledge/${kbId}/nodes/${nodeId}`));
}

/** The extracted text of a file, or a note's own body. */
export async function getKbNodeContent(kbId: string, nodeId: string): Promise<KbContent> {
  return unwrap(await client.get<Envelope<KbContent>>(`/knowledge/${kbId}/nodes/${nodeId}/content`));
}

/**
 * The stored bytes. The one route with no envelope — it answers the file itself
 * with its own `Content-Type`, so there is nothing for `unwrap` to read. The
 * caller owns the object URL it makes from this and must revoke it.
 */
export async function getKbNodeFile(kbId: string, nodeId: string): Promise<Blob> {
  const res = await client.get<Blob>(`/knowledge/${kbId}/nodes/${nodeId}/file`, { responseType: "blob" });
  return res.data;
}

/** A signed S3 link for a pdf or image, or a 404 when there is none — read `/file` then. */
export async function getKbNodeFileUrl(kbId: string, nodeId: string): Promise<KbFileUrl> {
  return unwrap(await client.get<Envelope<KbFileUrl>>(`/knowledge/${kbId}/nodes/${nodeId}/file-url`));
}

/** Re-extract and re-index a file — the way out of `ingest_status: "failed"`. */
export async function reindexKbNode(kbId: string, nodeId: string): Promise<KbNode> {
  return unwrap(await client.post<Envelope<KbNode>>(`/knowledge/${kbId}/nodes/${nodeId}/reindex`));
}

/* ------------------------------- links ----------------------------------- */

export async function listKbAgents(kbId: string): Promise<KbLink[]> {
  return unwrap(await client.get<Envelope<KbLink[]>>(`/knowledge/${kbId}/agents`));
}

/** Idempotent: re-linking an already linked agent is not an error. `mode` is sent only
    when given: omitted, an already linked agent keeps its mode. */
export async function linkKbAgents(kbId: string, agentIds: string[], mode?: KbLinkMode): Promise<KbLink[]> {
  const body = mode ? { agent_ids: agentIds, mode } : { agent_ids: agentIds };
  return unwrap(await client.post<Envelope<KbLink[]>>(`/knowledge/${kbId}/agents`, body));
}

export async function unlinkKbAgent(kbId: string, agentId: string): Promise<KbUnlinked> {
  return unwrap(await client.delete<Envelope<KbUnlinked>>(`/knowledge/${kbId}/agents/${agentId}`));
}

/* ------------------------------- hooks ----------------------------------- */

const NODES_POLL_MS = 10_000;

/** The server is still working on the node: its notes are compiling, or its description
    is coming (backend `FileOperations` in `business/files.py` describes only extracted text). */
export const isProcessing = (node: KbNode) =>
  node.file?.compile === "processing" ||
  (node.description_status === "pending" && node.file?.ingest_status === "extracted" && !!node.file.tokens);

export function useKnowledgeBases(agentId?: string) {
  return useQuery({
    queryKey: knowledgeKeys.list(agentId),
    queryFn: () => listKnowledgeBases(agentId),
  });
}

export function useKbNodes(kbId: string | null) {
  return useQuery({
    queryKey: knowledgeKeys.nodes(kbId ?? ""),
    queryFn: () => listKbNodes(kbId as string),
    enabled: !!kbId,
    refetchInterval: (query) => (query.state.data?.some(isProcessing) ? NODES_POLL_MS : false),
  });
}

/** Server-side full-text search. Idle until there is something to search for. */
export function useKbSearch(kbId: string | null, q: string) {
  return useQuery({
    queryKey: knowledgeKeys.search(kbId ?? "", q),
    queryFn: () => searchKb(kbId as string, q),
    enabled: !!kbId && q.trim().length > 0,
  });
}

/** `gcTime: 0` — the text is fetched once per open, and a reopen reads it fresh. Silent: a
    404 means the file has no text, and the file view says any other failure itself. */
export function useKbNodeContent(kbId: string, nodeId: string, enabled = true) {
  return useQuery({
    queryKey: knowledgeKeys.content(kbId, nodeId),
    queryFn: () => getKbNodeContent(kbId, nodeId),
    enabled,
    retry: false,
    gcTime: 0,
    meta: { silent: true },
  });
}

/** `gcTime: 0` — the link expires in 15 minutes, so a reopen asks for a new one. Silent: with no
    bucket (404) the viewer reads the bytes through the API instead, so it is not an error. */
export function useKbNodeFileUrl(kbId: string, nodeId: string, enabled = true) {
  return useQuery({
    queryKey: [...knowledgeKeys.all, "file-url", kbId, nodeId] as const,
    queryFn: () => getKbNodeFileUrl(kbId, nodeId),
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { silent: true },
  });
}

export function useKbAgents(kbId: string | null) {
  return useQuery({
    queryKey: knowledgeKeys.agents(kbId ?? ""),
    queryFn: () => listKbAgents(kbId as string),
    enabled: !!kbId,
  });
}

/* Every write invalidates the whole `knowledge` prefix. A base's `file_count`
   and `tokens` move when a node three levels down changes, and a link changes
   which bases a *different* query returns — narrowing this would be a rule to
   maintain, wrong the first time somebody adds a field (rule book §3 rule 5).
   Threads too: a thread's "From agent" set is the agent's linked bases, so a link,
   unlink, rename or delete there would otherwise read stale until its next refetch. */
function useKnowledgeMutation<V, T>(mutationFn: (vars: V) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: knowledgeKeys.all }),
        qc.invalidateQueries({ queryKey: threadKeys.all }),
      ]),
  });
}

export const useCreateKnowledgeBase = () => useKnowledgeMutation(createKnowledgeBase);

export const usePatchKnowledgeBase = () =>
  useKnowledgeMutation((v: { kbId: string; body: KbPatch }) => patchKnowledgeBase(v.kbId, v.body));

export const useDeleteKnowledgeBase = () => useKnowledgeMutation(deleteKnowledgeBase);

export const useCreateKbFolder = () =>
  useKnowledgeMutation((v: { kbId: string; body: KbFolderCreate }) => createKbFolder(v.kbId, v.body));

export const useUploadKbFiles = () =>
  useKnowledgeMutation((v: { kbId: string; uploads: KbUpload[]; parentId?: string }) =>
    uploadKbFiles(v.kbId, v.uploads, v.parentId),
  );

export const useCreateKbNote = () =>
  useKnowledgeMutation((v: { kbId: string; body: KbNoteCreate }) => createKbNote(v.kbId, v.body));

export const usePatchKbNode = () =>
  useKnowledgeMutation((v: { kbId: string; nodeId: string; body: KbNodePatch }) =>
    patchKbNode(v.kbId, v.nodeId, v.body),
  );

export const useDeleteKbNode = () =>
  useKnowledgeMutation((v: { kbId: string; nodeId: string }) => deleteKbNode(v.kbId, v.nodeId));

export const useReindexKbNode = () =>
  useKnowledgeMutation((v: { kbId: string; nodeId: string }) => reindexKbNode(v.kbId, v.nodeId));

export const useLinkKbAgents = () =>
  useKnowledgeMutation((v: { kbId: string; agentIds: string[]; mode?: KbLinkMode }) =>
    linkKbAgents(v.kbId, v.agentIds, v.mode),
  );

export const useUnlinkKbAgent = () =>
  useKnowledgeMutation((v: { kbId: string; agentId: string }) => unlinkKbAgent(v.kbId, v.agentId));
